import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { PageBody, PageHeader, ScopeBar } from '../components/AppShell';
import {
  Button,
  Card,
  CardHeader,
  Field,
  Icon,
  Loading,
  Pill,
  Segmented,
} from '../components/ui';
import { IntegrationsPanel, SecurityPanel } from './settings-panels';
import { AddParentOrganisation } from '../components/AddParentOrganisation';
import { MoveOffice } from '../components/MoveOffice';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { money } from '../lib/format';
import type { OrgNodeDetail, TreeNode } from '../lib/types';
import { useApi } from '../lib/useApi';
import './shared.css';

function flatten(node: TreeNode, depth = 0): Array<{ node: TreeNode; depth: number }> {
  return [
    { node, depth },
    ...(node.children ?? []).flatMap((child) => flatten(child, depth + 1)),
  ];
}

/**
 * Settings.
 *
 * One place for the things that were previously either scattered across three
 * screens or impossible — an office's name and the terms it passes down could
 * only be set when it was created, and never corrected.
 *
 * Three tabs. Organization is the only one that changes anything — the other
 * two report what the API is actually configured to do, which is set in its
 * environment and read once at startup. They are read-only for that reason,
 * not for lack of finishing: a form here would imply a restart-free change
 * that is not possible, and a control that lies is worse than no control.
 */

type Tab = 'organization' | 'integrations' | 'security';

const TAB_LABEL: Record<Tab, string> = {
  organization: 'Organization',
  integrations: 'Integrations',
  security: 'Security',
};
export function Settings() {
  const { user, can, refreshUser } = useAuth();
  const [tab, setTab] = useState<Tab>('organization');
  const tree = useApi<TreeNode>('/org/tree');
  const [nodeId, setNodeId] = useState<string | null>(null);
  const [moving, setMoving] = useState<TreeNode | null>(null);

  // Portal-wide configuration is an administrator's business; everyone else
  // sees only the office settings, so the tabs are not offered at all.
  const isAdmin = user?.role === 'l1_admin';
  const active: Tab = isAdmin ? tab : 'organization';

  const nodes = useMemo(() => (tree.data ? flatten(tree.data) : []), [tree.data]);
  const selectedId = nodeId ?? user?.nodeId ?? null;
  const selected = nodes.find((n) => n.node.id === selectedId)?.node ?? null;
  const detail = useApi<OrgNodeDetail>(
    selectedId ? `/org/nodes/${selectedId}` : null,
    [selectedId],
  );

  return (
    <>
      <PageHeader
        breadcrumb={['Administration', 'Settings']}
        title="Settings"
        actions={<Pill tone="neutral">{TAB_LABEL[active]}</Pill>}
      />

      <ScopeBar>
        {active === 'organization' ? (
          <>
            Changes here apply to an office and everything beneath it. You can
            only edit offices at or below <strong>{user?.nodeName}</strong>.
          </>
        ) : (
          <>
            Portal-wide, and read-only. These are set in the API's environment
            and read when it starts, so a change there needs a restart.
          </>
        )}
      </ScopeBar>

      <PageBody>
        {isAdmin && (
          <div style={{ marginBottom: 16 }}>
            <Segmented<Tab>
              label="Settings section"
              value={active}
              onChange={setTab}
              options={(Object.keys(TAB_LABEL) as Tab[]).map((t) => ({
                value: t,
                label: TAB_LABEL[t],
              }))}
            />
          </div>
        )}

        {active === 'integrations' && <IntegrationsPanel />}
        {active === 'security' && <SecurityPanel />}

        {active === 'organization' && tree.loading && (
          <Loading label="Loading your organisation…" />
        )}

        {active === 'organization' && tree.data && (
          <div className="split-2">
            <div className="stack">
              <Card>
                <CardHeader title="Which office" />
                <Field
                  label="Office"
                  hint="Everything below applies to the one you pick."
                  htmlFor="settings-node"
                >
                  <select
                    id="settings-node"
                    className="input select"
                    value={selectedId ?? ''}
                    onChange={(e) => setNodeId(e.target.value)}
                  >
                    {nodes.map(({ node, depth }) => (
                      <option key={node.id} value={node.id}>
                        {'  '.repeat(depth)}
                        {node.name}
                        <> · {node.level}</>
                      </option>
                    ))}
                  </select>
                </Field>

                {selected && can('org.move_node') && selected.id !== tree.data?.id && (
                  <div className="row" style={{ marginTop: 12 }}>
                    <Button onClick={() => setMoving(selected)}>
                      Move {selected.name} elsewhere…
                    </Button>
                  </div>
                )}
              </Card>

              {detail.data && can('org.move_node') && (
                <OfficeDetails
                  node={detail.data}
                  onSaved={() => {
                    detail.reload();
                    tree.reload();
                  }}
                />
              )}

              {tree.data && user?.nodeId === tree.data.id && user.role === 'l1_admin' && (
                <AddParentOrganisation
                  currentTop={tree.data.name}
                  onAdded={() => {
                    // The caller now stands at the new top, so the session's
                    // own idea of where it sits is stale until it is re-read.
                    void refreshUser().then(() => {
                      tree.reload();
                      detail.reload();
                    });
                  }}
                />
              )}

              {detail.data && !can('org.move_node') && (
                <Card>
                  <CardHeader title="Office details" />
                  <p className="muted" style={{ margin: 0 }}>
                    Your role cannot restructure the tree, so these are read-only.
                    An office principal or an administrator can change them.
                  </p>
                </Card>
              )}
            </div>

            <div className="stack">
              {detail.data && (
                <SplitsEditor
                  node={detail.data}
                  nodes={nodes.map((n) => n.node)}
                  onSaved={() => detail.reload()}
                />
              )}
            </div>
          </div>
        )}

        <MoveOffice
          node={moving}
          tree={tree.data ?? null}
          onClose={() => setMoving(null)}
          onMoved={() => {
            // Levels and terms may both have changed, and the caller's own
            // scope is unaffected only because a move stays inside it.
            tree.reload();
            detail.reload();
          }}
        />
      </PageBody>
    </>
  );
}

/** Name, principal, and the two terms an office passes down. */
function OfficeDetails({
  node,
  onSaved,
}: {
  node: OrgNodeDetail;
  onSaved: () => void;
}) {
  const [name, setName] = useState(node.name);
  const [principalName, setPrincipalName] = useState(node.principalName ?? '');
  const [bps, setBps] = useState(String(node.pricingFloor.basisPoints));
  const [perItem, setPerItem] = useState(String(node.pricingFloor.perItemCents));
  const [limit, setLimit] = useState(String(node.equipmentApprovalLimitCents / 100));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Switching office replaces what is being edited — and only that. Watching
  // the values too meant saving wiped its own confirmation, because the save
  // reloads the node and the reload looked like a change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setName(node.name);
    setPrincipalName(node.principalName ?? '');
    setBps(String(node.pricingFloor.basisPoints));
    setPerItem(String(node.pricingFloor.perItemCents));
    setLimit(String(node.equipmentApprovalLimitCents / 100));
    setError(null);
    setNotice(null);
  }, [node.id]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await api.patch<OrgNodeDetail & { descendantsAdjusted?: number }>(
        `/org/nodes/${node.id}`,
        {
          name: name.trim(),
          principalName: principalName.trim(),
          pricingFloorBasisPoints: Number(bps || 0),
          pricingFloorPerItemCents: Number(perItem || 0),
          equipmentApprovalLimitCents: Math.round(Number(limit || 0) * 100),
        },
      );

      const adjusted = saved.descendantsAdjusted ?? 0;
      // Say so when the change reached further than the office in front of them.
      setNotice(
        adjusted > 0
          ? `Saved. ${adjusted} office${adjusted === 1 ? '' : 's'} beneath it were brought in line.`
          : 'Saved.',
      );
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader title="Office details" meta={node.level} />
      <form className="stack" onSubmit={save}>
        {error && (
          <div className="notice notice--danger" role="alert">
            {Icon.alert({ size: 15 })}
            <span>{error}</span>
          </div>
        )}
        {notice && (
          <div className="notice notice--ok" role="status">
            {Icon.check({ size: 15 })}
            <span>{notice}</span>
          </div>
        )}

        <Field label="Name" htmlFor="office-name">
          <input
            id="office-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <Field label="Who runs it" htmlFor="office-principal">
          <input
            id="office-principal"
            className="input"
            value={principalName}
            onChange={(e) => setPrincipalName(e.target.value)}
          />
        </Field>

        <Field
          label="Pricing floor"
          hint="The lowest this office and everything beneath it may price. Raising it raises any office below that sits under the new figure."
          htmlFor="office-bps"
        >
          <div className="row" style={{ gap: 8 }}>
            <input
              id="office-bps"
              className="input"
              inputMode="numeric"
              style={{ maxWidth: 110 }}
              value={bps}
              onChange={(e) => setBps(e.target.value.replace(/\D/g, ''))}
              aria-label="Basis points"
            />
            <span className="muted">bps +</span>
            <input
              className="input"
              inputMode="numeric"
              style={{ maxWidth: 110 }}
              value={perItem}
              onChange={(e) => setPerItem(e.target.value.replace(/\D/g, ''))}
              aria-label="Per item in cents"
            />
            <span className="muted">¢ per item</span>
          </div>
        </Field>

        <Field
          label="Equipment approval limit"
          hint="Dollars this office may approve without asking the one above it. Lowering it clamps any office below that sits over the new figure."
          htmlFor="office-limit"
        >
          <input
            id="office-limit"
            className="input"
            inputMode="decimal"
            style={{ maxWidth: 160 }}
            value={limit}
            onChange={(e) => setLimit(e.target.value.replace(/[^\d.]/g, ''))}
          />
        </Field>

        <div className="row">
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** The revenue split applied to merchants boarded under this office. */
function SplitsEditor({
  node,
  nodes,
  onSaved,
}: {
  node: OrgNodeDetail;
  nodes: TreeNode[];
  onSaved: () => void;
}) {
  const { can } = useAuth();
  const editable = can('residual.set_splits');

  const [rows, setRows] = useState(node.splits);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Same here: keyed on the office, not on the splits it carries.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setRows(node.splits);
    setError(null);
    setNotice(null);
  }, [node.id]);

  const total = rows.reduce((sum, r) => sum + (Number(r.percent) || 0), 0);
  const balanced = Math.abs(total - 100) < 0.01;

  // Anything in this office's own chain may take a share. The detail response
  // carries no path, so the chain is walked from the tree by parentId.
  const chain = useMemo(() => {
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const ids: string[] = [];
    let cursor: TreeNode | undefined = byId.get(node.id);
    while (cursor) {
      ids.push(cursor.id);
      cursor = cursor.parentId ? byId.get(cursor.parentId) : undefined;
    }
    return ids;
  }, [nodes, node.id]);

  const candidates = nodes.filter(
    (n) => chain.includes(n.id) && !rows.some((r) => r.nodeId === n.id),
  );

  async function save() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.put(`/org/nodes/${node.id}/splits`, {
        splits: rows.map((r) => ({ nodeId: r.nodeId, percent: Number(r.percent) })),
      });
      setNotice('Saved. It applies to merchants boarded from now on, not to existing ones.');
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title="Revenue split"
        meta={`${total.toFixed(2).replace(/\.00$/, '')}% allocated`}
      />
      <div className="stack">
        <p className="muted" style={{ margin: 0 }}>
          How residual is divided on merchants boarded under {node.name}. Existing
          merchants keep the split they were boarded with.
        </p>

        {error && (
          <div className="notice notice--danger" role="alert">
            {Icon.alert({ size: 15 })}
            <span>{error}</span>
          </div>
        )}
        {notice && (
          <div className="notice notice--ok" role="status">
            {Icon.check({ size: 15 })}
            <span>{notice}</span>
          </div>
        )}

        {rows.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>
            Nothing set — this office inherits whatever its parent applies.
          </p>
        )}

        {rows.map((row, i) => (
          <div key={row.nodeId} className="row" style={{ gap: 8 }}>
            <span style={{ flex: 1, fontSize: 13.5 }}>
              {row.nodeName} <span className="muted">· {row.level}</span>
            </span>
            <input
              className="input"
              inputMode="decimal"
              style={{ maxWidth: 84 }}
              disabled={!editable}
              value={String(row.percent)}
              aria-label={`${row.nodeName} percent`}
              onChange={(e) => {
                const value = e.target.value.replace(/[^\d.]/g, '');
                setRows(rows.map((r, j) => (j === i ? { ...r, percent: Number(value) || 0 } : r)));
              }}
            />
            <span className="muted">%</span>
            {editable && (
              <Button
                size="sm"
                onClick={() => setRows(rows.filter((_, j) => j !== i))}
              >
                Remove
              </Button>
            )}
          </div>
        ))}

        {editable && candidates.length > 0 && (
          <Field label="Add a share" htmlFor="splits-add">
            <select
              id="splits-add"
              className="input select"
              value=""
              onChange={(e) => {
                const found = nodes.find((n) => n.id === e.target.value);
                if (!found) return;
                setRows([
                  ...rows,
                  {
                    nodeId: found.id,
                    nodeName: found.name,
                    level: found.level,
                    percent: 0,
                  },
                ]);
              }}
            >
              <option value="">Choose an office…</option>
              {candidates.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.name} · {n.level}
                </option>
              ))}
            </select>
          </Field>
        )}

        {editable && (
          <>
            {!balanced && rows.length > 0 && (
              <div className="notice notice--warn">
                {Icon.alert({ size: 15 })}
                <span>
                  The shares total {total.toFixed(2).replace(/\.00$/, '')}%. They have
                  to come to exactly 100 before this can be saved.
                </span>
              </div>
            )}
            <div className="row">
              <Button
                variant="primary"
                disabled={busy || !balanced || rows.length === 0}
                onClick={() => void save()}
              >
                {busy ? 'Saving…' : 'Save split'}
              </Button>
              <Button disabled={busy} onClick={() => setRows(node.splits)}>
                Reset
              </Button>
            </div>
          </>
        )}

        {!editable && (
          <p className="muted" style={{ margin: 0 }}>
            Your role can see the split but not change it — that needs
            residual.set_splits, which an office above you can grant.
          </p>
        )}

        <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
          Equipment approvals above {money(node.equipmentApprovalLimitCents)} go to
          the office above this one.
        </p>
      </div>
    </Card>
  );
}
